/**
 * Content for the Karriere page (`/careers`), from the website draft's "3i"
 * screen. German is the draft's source language; English is the site's toggle.
 */
import type { Bilingual } from "@/components/operators/landlord-data";

export type { Bilingual };

export const CAREERS_EMAIL = "careers@coliviq.de";

export const CAREER_VALUES: { name: Bilingual; body: Bilingual }[] = [
  {
    name: {
      de: "Erst selbst, dann für andere",
      en: "Ourselves first, then others",
    },
    body: {
      de: "Was wir bauen, nutzen wir in unseren eigenen WGs jeden Tag.",
      en: "What we build, we use in our own shared flats every day.",
    },
  },
  {
    name: { de: "Verantwortung übernehmen", en: "Taking ownership" },
    body: {
      de: "Du bekommst vom ersten Tag an eigene Themen — und die Freiheit, sie zu gestalten.",
      en: "You get your own areas from day one — and the freedom to shape them.",
    },
  },
  {
    name: { de: "Schnell entscheiden", en: "Deciding quickly" },
    body: {
      de: "Kurze Wege, keine Hierarchie-Schleifen. Gute Ideen gehen schnell live.",
      en: "Short paths, no hierarchy loops. Good ideas go live fast.",
    },
  },
  {
    name: { de: "Ehrlich bleiben", en: "Staying honest" },
    body: {
      de: "Wir sagen, was funktioniert und was nicht — zu Mietern, Vermietern und untereinander.",
      en: "We say what works and what does not — to tenants, to landlords, and to each other.",
    },
  },
];

export const CAREER_BENEFITS: { name: Bilingual; body: Bilingual }[] = [
  {
    name: { de: "Faire Vergütung", en: "Fair pay" },
    body: {
      de: "Attraktives Grundgehalt plus leistungsabhängiger Bonus.",
      en: "A solid base salary plus a performance bonus.",
    },
  },
  {
    name: { de: "Remote arbeiten", en: "Remote work" },
    body: {
      de: "Flexibel von zu Hause, mit festen Tagen im Büro in München.",
      en: "Flexible from home, with set days in the Munich office.",
    },
  },
  {
    name: { de: "Zusätzliche Urlaubstage", en: "Extra holiday" },
    body: {
      de: "Mehr Zeit für dich, über den gesetzlichen Urlaub hinaus.",
      en: "More time for you, beyond the statutory minimum.",
    },
  },
  {
    name: { de: "Deutschlandticket", en: "Deutschlandticket" },
    body: {
      de: "Wir übernehmen dein Ticket für Bus und Bahn.",
      en: "We cover your ticket for buses and trains.",
    },
  },
  {
    name: { de: "Wellpass", en: "Wellpass" },
    body: {
      de: "Fitnessstudios, Kurse und Schwimmbäder — über Wellpass.",
      en: "Gyms, classes and pools — through Wellpass.",
    },
  },
  {
    name: { de: "Kostenlose Getränke", en: "Drinks on us" },
    body: {
      de: "Kaffee, Wasser und Softdrinks im Büro sind frei.",
      en: "Coffee, water and soft drinks in the office are free.",
    },
  },
];

export const CAREER_STEPS: { no: string; name: Bilingual; body: Bilingual }[] =
  [
    {
      no: "1",
      name: { de: "Schreib uns", en: "Write to us" },
      body: {
        de: `CV oder ein paar Zeilen an ${CAREERS_EMAIL} — ein Anschreiben brauchst du nicht.`,
        en: `A CV or a few lines to ${CAREERS_EMAIL} — no cover letter needed.`,
      },
    },
    {
      no: "2",
      name: { de: "Lern uns kennen", en: "Meet us" },
      body: {
        de: "Wir melden uns bei dir für ein erstes Gespräch.",
        en: "We get in touch for a first conversation.",
      },
    },
    {
      no: "3",
      name: { de: "Willkommen", en: "Welcome" },
      body: {
        de: "Passt es für beide Seiten, freuen wir uns auf deinen ersten Tag.",
        en: "If it fits both ways, we look forward to your first day.",
      },
    },
  ];

export const FOUNDER_QUOTE = {
  quote: {
    de: "„Wir bauen nicht nur Wohnungen. Wir bauen Communities.“",
    en: "“We don’t just build flats. We build communities.”",
  },
  names: "Arber & Neel",
  role: { de: "Gründer von coliviq", en: "Founders of coliviq" },
};

export type CareerRole = {
  id: string;
  title: Bilingual;
  /** The title as the hero's open-roles link names it. */
  shortTitle: Bilingual;
  eyebrow: Bilingual;
  tags: Bilingual[];
  intro: Bilingual[];
  sections: { heading: Bilingual; items: Bilingual[] }[];
  facts: { label: Bilingual; value: Bilingual }[];
  factsNote: Bilingual;
  applyNote: Bilingual;
};

/**
 * Advertised roles, shown under "Offene Stellen". Empty means the section
 * falls back to the speculative-application copy alone.
 */
export const CAREER_ROLES: CareerRole[] = [
  {
    id: "werkstudent-content-social-media",
    title: {
      de: "Werkstudent:in Content-Strategie & Social Media (m/w/d)",
      en: "Working Student, Content Strategy & Social Media (f/m/d)",
    },
    shortTitle: {
      de: "Werkstudent:in Content & Social Media",
      en: "Working Student, Content & Social Media",
    },
    eyebrow: {
      de: "Werkstudent:in · Marketing",
      en: "Working student · Marketing",
    },
    tags: [
      { de: "München, hybrid", en: "Munich, hybrid" },
      { de: "14 € pro Stunde", en: "€14 per hour" },
      { de: "Start ab sofort", en: "Starts now" },
    ],
    intro: [
      {
        de: "Wohnen in Deutschland ist kompliziert. Wir machen es einfacher. coliviq betreibt möblierte Coliving-WGs und übernimmt für Eigentümer:innen die komplette Verwaltung, von der Vermietung bis zum Auszug. Unsere eigene Plattform macht das fast vollständig digital: Eigentümer:innen verfolgen alles live, Bewohner:innen regeln ihren Alltag in einer App.",
        en: "Renting in Germany is complicated. We make it simpler. coliviq runs furnished coliving flats and takes on the full management for owners, from letting to move-out. Our own platform makes almost all of it digital: owners follow everything live, and residents run their day-to-day in an app.",
      },
      {
        de: "Heute sind wir in München und Augsburg. Als Nächstes expandieren wir deutschlandweit. Dafür suchen wir jemanden, der unsere Content-Strategie entwickelt und selbst umsetzt. Du bekommst keine Aufgabenliste, du bekommst die Kanäle. Was dort passiert, entscheidest du.",
        en: "Today we are in Munich and Augsburg. Next, we expand across Germany. For that we are looking for someone to build our content strategy and carry it out. You don’t get a task list, you get the channels. What happens there is your call.",
      },
    ],
    sections: [
      {
        heading: { de: "Was du machst", en: "What you’ll do" },
        items: [
          {
            de: "Du entwickelst unsere Content-Strategie für zwei Zielgruppen: Bewohner:innen auf Instagram und TikTok, Eigentümer:innen und Partner auf LinkedIn.",
            en: "You build our content strategy for two audiences: residents on Instagram and TikTok, owners and partners on LinkedIn.",
          },
          {
            de: "Du legst fest, welche Themen, Formate und Botschaften wir spielen, wie oft wir posten und was wir weglassen.",
            en: "You decide which topics, formats and messages we run, how often we post and what we leave out.",
          },
          {
            de: "Du übersetzt die Strategie in einen festen Contentplan und setzt ihn selbst um.",
            en: "You turn the strategy into a fixed content plan and carry it out yourself.",
          },
          {
            de: "Du drehst und schneidest Reels, TikToks und Fotos in unseren WGs, von Room Tours bis Bewohner-Content.",
            en: "You shoot and edit Reels, TikToks and photos in our flats, from room tours to resident content.",
          },
          {
            de: "Du entwickelst, wie wir neue Städte und WGs während der Expansion launchen und sichtbar machen.",
            en: "You work out how we launch new cities and flats during the expansion and make them visible.",
          },
          {
            de: "Du misst, was deine Arbeit bringt, vor allem Reichweite und Anfragen, und stellst uns regelmäßig vor, was du daraus ableitest und was du änderst.",
            en: "You measure what your work achieves, above all reach and enquiries, and regularly show us what you take from it and what you change.",
          },
        ],
      },
      {
        heading: { de: "Was du mitbringst", en: "What you bring" },
        items: [
          {
            de: "Du bist eingeschriebene:r Student:in, idealerweise in Marketing, Kommunikation, Medien oder einem ähnlichen Fach.",
            en: "You are an enrolled student, ideally in marketing, communications, media or a similar subject.",
          },
          {
            de: "Du denkst in Zielgruppen und Zielen, nicht in einzelnen Posts, und kannst begründen, warum etwas funktioniert.",
            en: "You think in audiences and goals rather than single posts, and you can explain why something works.",
          },
          {
            de: "Du hast schon etwas aus eigenem Antrieb aufgebaut, einen Account, ein Projekt oder einen Verein. Erzähl uns davon.",
            en: "You have built something on your own initiative, an account, a project or a club. Tell us about it.",
          },
          {
            de: "Du drehst und schneidest sicher mit dem Smartphone. Mit CapCut, Canva oder ähnlichen Tools kennst du dich aus.",
            en: "You shoot and edit confidently on a smartphone and know your way around CapCut, Canva or similar tools.",
          },
          {
            de: "Du arbeitest selbstständig, hältst einen Rhythmus ein und meldest dich, wenn etwas nicht läuft, nicht erst, wenn jemand fragt.",
            en: "You work independently, keep a steady rhythm and speak up when something isn’t working, before anyone has to ask.",
          },
          {
            de: "Du sprichst sehr gut Deutsch und gut Englisch. Viele unserer Bewohner:innen kommen aus dem Ausland.",
            en: "You speak excellent German and good English. Many of our residents come from abroad.",
          },
        ],
      },
      {
        heading: { de: "Was wir bieten", en: "What we offer" },
        items: [
          {
            de: "Echte Verantwortung ab dem ersten Tag. Du baust unsere Content-Strategie während der deutschlandweiten Expansion auf.",
            en: "Real responsibility from day one. You build our content strategy during the expansion across Germany.",
          },
          {
            de: "Kreativer Freiraum. Alles, was du für uns produzierst, darfst du in deinem Portfolio zeigen.",
            en: "Creative freedom. Everything you make for us can go in your portfolio.",
          },
          {
            de: "Direkte Zusammenarbeit mit den Gründern und Einblick in Proptech, Vermietung und Plattform-Aufbau.",
            en: "Working directly with the founders, with insight into proptech, letting and building a platform.",
          },
          {
            de: "Equipment für deine Drehs: Gimbal, Licht, Mikro, eine Drohne und eine DJI Osmo Pocket 3.",
            en: "Equipment for your shoots: gimbal, lighting, mic, a drone and a DJI Osmo Pocket 3.",
          },
          {
            de: "Die Möglichkeit, nach deinem Studium bei uns einzusteigen.",
            en: "The option to join us full-time after your studies.",
          },
          {
            de: "Gelegentliche Drehs in anderen Städten, Reisekosten übernehmen wir.",
            en: "Occasional shoots in other cities, with travel costs covered.",
          },
        ],
      },
    ],
    facts: [
      {
        label: { de: "Umfang", en: "Hours" },
        value: { de: "20 Std./Woche", en: "20 hrs/week" },
      },
      {
        label: { de: "Vergütung", en: "Pay" },
        value: { de: "14 € pro Stunde", en: "€14 per hour" },
      },
      {
        label: { de: "Arbeitsort", en: "Location" },
        value: { de: "München, hybrid", en: "Munich, hybrid" },
      },
      {
        label: { de: "Start", en: "Start" },
        value: { de: "Ab sofort", en: "Now" },
      },
    ],
    factsNote: {
      de: "Flexibel neben dem Studium planbar. In Prüfungsphasen kannst du deine Stunden reduzieren.",
      en: "Planned flexibly around your studies. You can cut your hours during exams.",
    },
    applyNote: {
      de: `CV und ein paar Zeilen an ${CAREERS_EMAIL} reichen. Zeig uns gern einen Account oder ein Projekt, das du aufgebaut hast.`,
      en: `A CV and a few lines to ${CAREERS_EMAIL} is enough. Show us an account or project you have built, if you have one.`,
    },
  },
];
